import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Separator } from "@/components/ui/separator"
import { SidebarMenuButton } from "@/components/ui/sidebar"
import { Newspaper } from "lucide-react"
import { useState } from "react"
import { formatChangelogDate, parseChangelog } from "@/lib/changelog"
import changelogSource from "../../CHANGELOG.md?raw"

const releases = parseChangelog(changelogSource)
const initialReleaseCount = 5
const seenVersionStorageKey = "farol-changelog-seen-version-v1"

function readSeenVersion() {
  try {
    return window.localStorage.getItem(seenVersionStorageKey)
  } catch {
    return null
  }
}

function storeSeenVersion(version: string) {
  try {
    window.localStorage.setItem(seenVersionStorageKey, version)
  } catch {
    // The indicator simply shows again when browser storage is unavailable.
  }
}

export function ChangelogDialog({ version, className }: { version: string; className?: string }) {
  const [showAll, setShowAll] = useState(false)
  const [hasUnseenRelease, setHasUnseenRelease] = useState(() => releases.some((release) => release.version === version) && readSeenVersion() !== version)
  const visibleReleases = showAll ? releases : releases.slice(0, initialReleaseCount)

  return (
    <Dialog
      onOpenChange={(open) => {
        if (open && hasUnseenRelease) {
          storeSeenVersion(version)
          setHasUnseenRelease(false)
        }
        if (!open) setShowAll(false)
      }}
    >
      <DialogTrigger render={<SidebarMenuButton type="button" tooltip="Novidades do Farol" className={className} aria-label={`Novidades do Farol${hasUnseenRelease ? " (há novidades)" : ""}`} />}>
          <span className="relative flex shrink-0">
            <Newspaper />
            {hasUnseenRelease ? <span aria-hidden="true" data-slot="unseen-indicator" className="absolute -top-1 -right-1 size-2 rounded-full bg-primary ring-2 ring-sidebar" /> : null}
          </span>
          <span>Novidades do Farol</span>
        </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Novidades do Farol</DialogTitle>
          <DialogDescription>O que mudou em cada versão do app.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-6">
          {visibleReleases.map((release, index) => (
            <section key={release.version} aria-labelledby={`release-${release.version}`} className="grid gap-3">
              {index > 0 ? <Separator /> : null}
              <div className="flex flex-wrap items-center gap-2">
                <h3 id={`release-${release.version}`} className="text-sm font-semibold tabular-nums">Versão {release.version}</h3>
                {release.version === version ? <Badge variant="secondary">Versão atual</Badge> : null}
                {release.date ? <span className="ml-auto text-xs text-muted-foreground">{formatChangelogDate(release.date)}</span> : null}
              </div>
              {release.intro.map((paragraph) => <p key={paragraph} className="text-sm text-muted-foreground">{paragraph}</p>)}
              {release.groups.map((group, groupIndex) => (
                <div key={`${group.title}-${groupIndex}`} className="grid gap-1.5">
                  {group.title ? <h4 className="text-xs font-medium text-muted-foreground">{group.title}</h4> : null}
                  <ul className="grid list-disc gap-1 pl-5 text-sm">
                    {group.items.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              ))}
            </section>
          ))}
          {visibleReleases.length < releases.length ? (
            <Button type="button" variant="outline" className="w-full" onClick={() => setShowAll(true)}>
              Ver versões anteriores
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}
